/**
 * Global design constants used throughout the greeting card builder.
 * These values are standardized across all designs to ensure consistency.
 */

/**
 * Photo slot zoom configuration
 */
export const PHOTO_ZOOM = {
  min: 0.5,
  max: 3,
  initial: 1,
} as const

/**
 * Rotation step in degrees for photo placement
 */
export const ROTATION_STEP_DEG = 90

/**
 * DPI (dots per inch) requirements for photo quality
 */
export const RECOMMENDED_DPI = 300
export const MIN_DPI = 150

/**
 * Minimum size for QR decorations to remain scannable.
 */
export const MIN_QR_DECORATION_SIZE = 300

/**
 * QR decoration height multiplier to fit caption without side gutters.
 */
export const QR_DECORATION_HEIGHT_SCALE = 1.14

/**
 * Minimum QR decoration height to preserve scan area and caption legibility.
 */
export const MIN_QR_DECORATION_HEIGHT = Math.round(MIN_QR_DECORATION_SIZE * QR_DECORATION_HEIGHT_SCALE)

/**
 * Text layer font size constraints (as percentage of template height, 0-1 range)
 * - MAX: Absolute ceiling that auto-resize cannot exceed (allows short text to grow)
 * - DEFAULT_FALLBACK: Used when a layer doesn't specify defaultFontSize
 */
export const TEXT_LAYER_MAX_FONT_SIZE = 0.4 // 40% of template height
export const TEXT_LAYER_DEFAULT_FONT_SIZE = 0.05 // 5% of template height

/**
 * Available font families for inside message text
 * Each font includes fallback fonts for browser compatibility
 */
export const FONT_FAMILIES = [
  // Sans-serif
  'Roboto, Arial, sans-serif',
  'Archivo, Arial, sans-serif',
  'Montserrat, Arial, sans-serif',
  'Poppins, Arial, sans-serif',
  'Raleway, Arial, sans-serif',
  'Quicksand, Arial, sans-serif',
  'Oswald, Arial, sans-serif',
  'Comfortaa, Arial, sans-serif',
  'Baloo 2, Arial, sans-serif',
  // Script & Cursive
  'Satisfy, cursive',
  'Lobster, cursive',
  'Shadows Into Light, cursive',
  'Dancing Script, cursive',
  'Caveat, cursive',
  'Pacifico, cursive',
  'Great Vibes, cursive',
  'Indie Flower, cursive',
  'Sacramento, cursive',
  'Cookie, cursive',
  'Courgette, cursive',
  'Kalam, cursive',
  // Fun & Playful
  'Bangers, cursive',
  'Permanent Marker, cursive',
  'Fredoka, sans-serif',
  'Righteous, cursive',
  'Chewy, cursive',
  'Gloria Hallelujah, cursive',
  'Patrick Hand, cursive',
  'Rock Salt, cursive',
  'Amatic SC, cursive',
  'Creepster, cursive',
  'Lilita One, cursive',
  'Luckiest Guy, cursive',
  'Bubblegum Sans, cursive',
  // Serif
  'Noto Serif, serif',
  'EB Garamond, serif',
  'Playfair Display, serif',
  'Lora, serif',
  'Merriweather, serif',
  // Monospace
  'DM Mono, monospace',
] as const

/**
 * Helper to convert font families array into select menu items
 * Extracts the primary font name as the label and includes font style for preview
 */
export function getFontMenuItems() {
  return FONT_FAMILIES.map((f) => ({
    label: f.split(',')[0],
    value: f,
    style: { fontFamily: f },
  }))
}

/**
 * Available font sizes in points for inside message text
 */
export const FONT_SIZES = [12, 14, 16, 18, 20, 22, 24, 28, 32, 36, 42, 48] as const

/**
 * Helper to convert font sizes array into select menu items
 */
export function getFontSizeMenuItems() {
  return FONT_SIZES.map((s) => ({
    label: `${s} pt`,
    value: s as number,
  }))
}
